import { getApiUrl } from "@/utils/api";
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import { useIntegrationVisibility } from "@/hooks/useIntegrationVisibility";
import { Loader2, AlertTriangle, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { resolveImageUrl } from "@/utils/imageUrl";

const API = getApiUrl();
const FALLBACK_LOGO_URL = "https://customer-assets.emergentagent.com/job_realty-gallery/artifacts/zzc7uqc2_Screenshot_20260119_035617_Chrome.ico";

// Detect known in-app/WebView browsers that Google blocks for OAuth
// (Error 403: disallowed_useragent). Covers Instagram, Facebook, Messenger,
// LinkedIn, Twitter/X, WhatsApp, Telegram, Snapchat, Pinterest, Line, WeChat,
// TikTok, Slack, plus generic Android `wv` and iOS in-app webviews.
const isInAppBrowser = (ua) => {
  if (!ua) return false;
  const patterns = [
    /Instagram/i, /FBAN|FBAV|FBIOS|FB_IAB/i, /Messenger/i, /LinkedInApp/i,
    /Twitter|TwitterApp/i, /WhatsApp/i, /Telegram/i, /Snapchat/i, /Pinterest/i,
    /Line\//i, /MicroMessenger/i, /TikTok|Bytedance|musical_ly/i, /Slack/i,
    /; wv\)/i, // Android WebView
  ];
  return patterns.some((re) => re.test(ua));
};

export default function PropertyAdminLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const propertyIdFromUrl = searchParams.get("property_id");
  const agentIdFromUrl = searchParams.get("agent_id");
  // Set when a gated CMS page (see routes/address_lookup-adjacent
  // requires_agent_login flag) sent the visitor here — we bounce them back
  // to it once they're signed in instead of landing on the dashboard.
  const redirectTarget = searchParams.get("redirect");
  const [checkingSession, setCheckingSession] = useState(true);
  const [inApp, setInApp] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(FALLBACK_LOGO_URL);
  const [companyName, setCompanyName] = useState("SellLocalHomes");
  const { isEnabled } = useIntegrationVisibility();
  const googleSsoEnabled = isEnabled("google_sso");

  // Fetch the company logo & name from Site Styles — single source of
  // truth set in Admin Dashboard -> Admin -> Site Styles -> Company Logo.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/site-styles`)
      .then((res) => {
        if (cancelled) return;
        const styles = res.data || {};
        if (styles.companyLogo) setCompanyLogo(resolveImageUrl(styles.companyLogo));
        if (styles.navLogoText) setCompanyName(styles.navLogoText);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    // Lets the dashboard's OAuth callback know to send a failed sign-in
    // (e.g. a disabled account) back to this dedicated page instead of
    // the public home page sign-in modal.
    try { sessionStorage.setItem("slh_agent_login_entry", "property_admin_login"); } catch { /* storage unavailable */ }
    const dashboardUrl = `${window.location.origin}/property-admin/dashboard`;
    // `next` rides along inside the same redirect target Emergent Auth
    // bounces back to (it just appends #session_id=... to whatever URL we
    // give it), so the dashboard can read it after the callback resolves.
    const redirectUrl = redirectTarget
      ? `${dashboardUrl}?next=${encodeURIComponent(redirectTarget)}`
      : dashboardUrl;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const copyLoginLink = async () => {
    const url = `${window.location.origin}/property-admin/login`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — paste it into Safari or Chrome");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy automatically — long-press the URL bar to copy");
    }
  };

  useEffect(() => {
    document.title = "Customer Sign In";
    setInApp(isInAppBrowser(navigator.userAgent));

    // Surface a failed sign-in (e.g. a disabled account) bounced back here
    // by the dashboard's OAuth callback — see handleGoogleSignIn above.
    const err = searchParams.get("login_error");
    if (err) {
      setLoginError(err);
      const params = new URLSearchParams(window.location.search);
      params.delete("login_error");
      const qs = params.toString();
      window.history.replaceState({}, document.title, window.location.pathname + (qs ? `?${qs}` : ""));
    }

    // Auto-login: Bypass login page if active session exists
    // This checks for an active SSO session and redirects automatically
    let cancelled = false;
    (async () => {
      try {
        // Check if user has an active property admin session
        await axios.get(`${API}/property-admin/me`, { withCredentials: true });
        
        // If session is active, go straight to the page they wanted (if any)
        if (!cancelled) navigate(redirectTarget || "/property-admin/dashboard", { replace: true });
      } catch {
        // No active session - show login page
        if (!cancelled) setCheckingSession(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // Use site styles if helpful (no-op if missing — keeps page resilient)
  try { useSiteStyles(); } catch { /* ignore — provider not mounted in some test contexts */ }

  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30" data-testid="property-admin-login-checking">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">Checking session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30" data-testid="property-admin-login-page">
      <div className="w-full max-w-md p-8">
        <div className="glass-card p-10 space-y-8">
          {/* Logo & Title */}
          <div className="text-center space-y-4">
            <img src={companyLogo} alt={companyName} className="h-16 w-16 mx-auto object-contain" data-testid="agent-login-logo" />
            <h1 className="font-serif text-3xl font-medium">Register/Sign-In</h1>
            <p className="text-muted-foreground text-sm">
              Sign in with your Google account to manage your listings.
            </p>
          </div>

          <div className="section-divider mx-auto" />

          {(propertyIdFromUrl || agentIdFromUrl) && (
            <div className="bg-muted/40 border border-dashed border-border p-3 text-xs text-muted-foreground" data-testid="agent-login-context">
              {propertyIdFromUrl && <>You're signing in for property <code className="text-xs">{propertyIdFromUrl}</code>. </>}
              {agentIdFromUrl && <>Customer <code className="text-xs">{agentIdFromUrl}</code></>}
            </div>
          )}

          {loginError && (
            <div className="border-2 border-red-300 bg-red-50 p-4 space-y-1 text-sm" data-testid="agent-login-error-banner" role="alert">
              <div className="flex gap-2 items-start">
                <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <p className="text-red-800 leading-relaxed">{loginError}</p>
              </div>
            </div>
          )}

          {inApp && (
            <div className="border-2 border-amber-400 bg-amber-50 p-4 space-y-3" data-testid="webview-warning-banner">
              <div className="flex gap-2 items-start">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-1 text-sm">
                  <p className="font-medium text-amber-900">Open in your browser to sign in</p>
                  <p className="text-amber-800 text-xs leading-relaxed">
                    Google blocks sign-in inside in-app previews (Instagram, Facebook, Gmail, etc.).
                    Open this page in <strong>Safari</strong> or <strong>Chrome</strong> to continue.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                onClick={copyLoginLink}
                variant="outline"
                size="sm"
                className="w-full rounded-none bg-white border-amber-300 hover:bg-amber-100 text-amber-900"
                data-testid="copy-login-link-btn"
              >
                {copied ? (
                  <><Check className="h-4 w-4 mr-2" /> Link copied — paste in Safari/Chrome</>
                ) : (
                  <><Copy className="h-4 w-4 mr-2" /> Copy sign-in link</>
                )}
              </Button>
            </div>
          )}

          {/* Google Sign In — only auth method */}
          {googleSsoEnabled ? (
            <Button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={inApp}
              className="w-full h-14 bg-white border border-border hover:bg-muted/50 text-foreground rounded-none text-base font-medium flex items-center justify-center gap-3 btn-lift disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="property-admin-google-signin-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Sign in with Google
            </Button>
          ) : (
            <p className="text-sm text-center text-muted-foreground py-2" data-testid="property-admin-signin-disabled">
              Sign-in is currently unavailable. Please contact the site administrator.
            </p>
          )}

          <p className="text-xs text-center text-muted-foreground">
            New to SellLocalHomes? Sign in with Google and we'll set up your
            account automatically.
          </p>
        </div>

        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-sm text-muted-foreground hover:text-foreground link-underline"
            data-testid="back-to-site-link"
          >
            ← Back to SellLocalHomes
          </a>
        </div>
      </div>
    </div>
  );
}
